import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, challengesAPI, quranAPI } from '../utils/api';
import { Trophy, Users, Plus, Copy, ChevronLeft, Check } from 'lucide-react';
import { toast } from 'sonner';

const Challenges = () => {
  const { challengeId } = useParams();
  if (challengeId) return <ChallengeDetail challengeId={challengeId} />;
  return <ChallengeList />;
};

const ChallengeList = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const accent = isKidsMode ? 'amber' : 'teal';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, mine] = await Promise.all([authAPI.getMe(), challengesAPI.my()]);
        if (cancelled) return;
        setUser(u.data);
        setItems(mine.data?.items || []);
      } catch {
        if (!cancelled) toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length && d[0]?.msg) return d[0].msg;
    return fallback;
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const r = await challengesAPI.create(newName.trim(), 114);
      toast.success(lang === 'tr' ? 'Challenge oluşturuldu! Kodu paylaş.' : 'Created! Share the code.');
      setNewName('');
      const mine = await challengesAPI.my();
      setItems(mine.data?.items || []);
      navigate(`/challenges/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, lang === 'tr' ? 'Oluşturulamadı' : 'Failed'));
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    try {
      const r = await challengesAPI.join(joinCode.trim().toUpperCase());
      toast.success(lang === 'tr' ? 'Katıldın!' : 'Joined!');
      setJoinCode('');
      navigate(`/challenges/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, lang === 'tr' ? 'Kod bulunamadı' : 'Code not found'));
    }
  };

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Grup Hatim' : 'Group Hatim'}>
      <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <header className="mb-2">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Topluluk' : 'Community'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Grup Hatim Challenge' : 'Group Hatim Challenge'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr'
              ? 'Arkadaşlarınla birlikte 114 sureyi hedefleyen bir hatim grubu oluştur. Her üye okuduğu sureleri ekler.'
              : 'Create a group hatim aiming at all 114 surahs together. Each member contributes their read surahs.'}
          </p>
        </header>

        {/* Create + Join cards */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-800'} flex items-center justify-center`}>
                <Plus className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Yeni Challenge Oluştur' : 'Create Challenge'}
              </h3>
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={80}
              placeholder={lang === 'tr' ? 'Örn: Ramazan Hatmi 2026' : 'e.g. Ramadan Hatim 2026'}
              data-testid="challenge-name-input"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 mb-3"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim()}
              data-testid="challenge-create-btn"
              className={`w-full py-2.5 rounded-xl font-semibold text-white text-sm ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'} disabled:opacity-50`}
            >
              {lang === 'tr' ? 'Oluştur' : 'Create'}
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-800'} flex items-center justify-center`}>
                <Users className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Bir Challenge\'a Katıl' : 'Join Challenge'}
              </h3>
            </div>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="6-HANELI-KOD"
              data-testid="challenge-join-input"
              className="w-full px-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm uppercase tracking-widest text-center font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/40 mb-3"
            />
            <button
              type="button"
              onClick={handleJoin}
              disabled={joinCode.trim().length < 6}
              data-testid="challenge-join-btn"
              className={`w-full py-2.5 rounded-xl font-semibold text-white text-sm ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'} disabled:opacity-50`}
            >
              {lang === 'tr' ? 'Katıl' : 'Join'}
            </button>
          </div>
        </div>

        {/* My challenges */}
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
            {lang === 'tr' ? 'Benim Challenge\'larım' : 'My Challenges'}
          </h2>
          {loading ? (
            <div className="text-center py-8 text-stone-500">{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-stone-500 bg-stone-50 rounded-2xl border border-dashed border-stone-200">
              {lang === 'tr' ? 'Henüz katıldığın bir challenge yok.' : 'No challenges yet.'}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/challenges/${c.id}`)}
                  data-testid={`challenge-card-${c.id}`}
                  className="text-left bg-white rounded-2xl border border-stone-200 p-5 hover:border-stone-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.name}</h3>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${isKidsMode ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-700'}`}>{c.code}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-stone-500 mb-2">
                    <span>{c.member_count} {lang === 'tr' ? 'üye' : 'members'}</span>
                    <span>{c.completed_surah_count} / {c.target_surahs} {lang === 'tr' ? 'sure' : 'surahs'}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-stone-100 overflow-hidden">
                    <div
                      className={`h-full ${isKidsMode ? 'bg-amber-500' : 'bg-teal-600'}`}
                      style={{ width: `${c.progress_percent}%` }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
};

const ChallengeDetail = ({ challengeId }) => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const [u, c, s] = await Promise.all([
      authAPI.getMe(),
      challengesAPI.get(challengeId),
      quranAPI.getSurahList(),
    ]);
    setUser(u.data);
    setChallenge(c.data);
    setSurahs(s.data?.data || []);
  };

  useEffect(() => {
    load().catch(() => toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed')).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  const handleContribute = async (surahNumber) => {
    try {
      const r = await challengesAPI.contribute(challengeId, surahNumber);
      setChallenge(r.data);
      toast.success(lang === 'tr' ? 'Eklendi! Allah kabul etsin.' : 'Added! May Allah accept.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (Array.isArray(d) && d[0]?.msg) || (lang === 'tr' ? 'Eklenemedi' : 'Failed');
      toast.error(msg);
    }
  };

  const copyCode = () => {
    if (!challenge?.code) return;
    navigator.clipboard?.writeText(challenge.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error('Kopyalanamadı'));
  };

  if (loading || !challenge) {
    return (
      <AppShell user={user}>
        <div className="max-w-4xl mx-auto p-8 text-center text-stone-500">{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      </AppShell>
    );
  }

  const coveredSet = new Set(challenge.covered_surahs || []);

  return (
    <AppShell user={user} title={challenge.name}>
      <div className="max-w-5xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <button type="button" onClick={() => navigate('/challenges')} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900" data-testid="challenge-back">
          <ChevronLeft className="w-4 h-4" /> {lang === 'tr' ? 'Challenges' : 'Challenges'}
        </button>

        {/* Header */}
        <div className={`rounded-3xl border-2 p-7 ${isKidsMode ? 'bg-amber-50/60 border-amber-200' : 'bg-teal-50/60 border-teal-200'}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">{lang === 'tr' ? 'Grup Hatim' : 'Group Hatim'}</p>
              <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{challenge.name}</h1>
              <p className="text-sm text-stone-600 mt-1">
                {challenge.member_count} {lang === 'tr' ? 'üye · katkı:' : 'members · contributions:'} {challenge.contributions?.length || 0}
              </p>
            </div>
            <button
              type="button"
              onClick={copyCode}
              data-testid="challenge-copy-code"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-stone-200 text-sm font-mono font-bold tracking-widest hover:border-stone-400"
            >
              {challenge.code}
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-stone-500" />}
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-sm font-semibold text-stone-700 mb-2">
              <span>{challenge.completed_surah_count} / {challenge.target_surahs}</span>
              <span>{challenge.progress_percent}%</span>
            </div>
            <div className="w-full h-3 rounded-full bg-white overflow-hidden border border-stone-200">
              <div className={`h-full ${isKidsMode ? 'bg-amber-500' : 'bg-teal-600'}`} style={{ width: `${challenge.progress_percent}%` }} />
            </div>
          </div>

          {challenge.completed_at && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold">
              <Trophy className="w-4 h-4" />
              {lang === 'tr' ? 'Hatim tamamlandı! Allah kabul etsin.' : 'Hatim completed! May Allah accept.'}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        {challenge.leaderboard?.length > 0 && (
          <section className="bg-white rounded-2xl border border-stone-200 p-5">
            <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
              {lang === 'tr' ? 'Liderlik' : 'Leaderboard'}
            </h2>
            <div className="space-y-2">
              {challenge.leaderboard.slice(0, 10).map((m, idx) => (
                <div key={m.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-amber-200 text-amber-900' : 'bg-stone-200 text-stone-700'}`}>{idx + 1}</span>
                  <span className="flex-1 text-sm font-semibold text-stone-800">{m.name}</span>
                  <span className="text-xs text-stone-500">{m.surah_count} {lang === 'tr' ? 'sure' : 'surahs'}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Surah picker */}
        <section className="bg-white rounded-2xl border border-stone-200 p-5">
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-3 font-semibold">
            {lang === 'tr' ? 'Sure Ekle (Okuduğun)' : 'Add a Surah (One You Read)'}
          </h2>
          <p className="text-xs text-stone-500 mb-4">
            {lang === 'tr' ? 'Çoktan eklenen sureler grup adına sayılır — tek bir kez katkıda bulunulabilir.' : 'Already-covered surahs count for the group; one contribution per surah.'}
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-10 gap-2">
            {surahs.map((s) => {
              const covered = coveredSet.has(s.number);
              return (
                <button
                  key={s.number}
                  type="button"
                  onClick={() => !covered && handleContribute(s.number)}
                  disabled={covered}
                  data-testid={`challenge-contrib-${s.number}`}
                  className={`p-2 rounded-lg text-xs font-bold transition-all border ${covered ? 'bg-emerald-100 text-emerald-700 border-emerald-300 cursor-default' : 'bg-stone-50 text-stone-700 border-stone-200 hover:border-stone-400 hover:bg-white'}`}
                  title={s.name_en}
                >
                  {s.number}
                  {covered && <span className="block text-[10px]">✓</span>}
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default Challenges;
